import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp, UNITS, convertUnit } from '../contexts/AppContext';
import { generateRecommendations } from '../utils/recommendations';
import { downloadRecipeTemplate } from '../utils/csvTemplates';
import { generateReport } from '../utils/reportGenerator';
import ContactFormModal from '../components/ContactFormModal';
import WizardWelcome from '../components/wizard/WizardWelcome';
import WizardIngredients from '../components/wizard/WizardIngredients';
import WhyBox from '../components/wizard/WhyBox';
import {
  Pizza, ArrowRight, ArrowLeft, Check, Plus, X, Download,
  ChefHat, DollarSign, Calculator, Star,
  AlertTriangle, CheckCircle, Lightbulb, Target, Sparkles,
  BookOpen, ExternalLink, Info, Upload
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

const STEPS = [
  { id: 'welcome', label: 'Welcome', icon: Pizza },
  { id: 'ingredients', label: 'Ingredients', icon: Upload },
  { id: 'recipe', label: 'Recipe', icon: ChefHat },
  { id: 'labor', label: 'Labor', icon: DollarSign },
  { id: 'costs', label: 'Fixed Costs', icon: Calculator },
  { id: 'results', label: 'Report Card', icon: Star },
];

const formatCurrency = (v) => `$${(v || 0).toFixed(2)}`;

const TIP_ICONS = {
  critical: AlertTriangle,
  warning: AlertTriangle,
  success: CheckCircle,
  bestpractice: Lightbulb,
  info: Target,
};
const TIP_COLORS = {
  critical: 'text-[#E23D28]',
  warning: 'text-[#FFC107]',
  success: 'text-[#2E7D32]',
  bestpractice: 'text-[#FFC107]',
  info: 'text-[#2A2A2A]',
};
const TIP_BORDERS = {
  critical: 'border-[#E23D28]',
  warning: 'border-[#FFC107]',
  success: 'border-[#2E7D32]',
  bestpractice: 'border-[#2A2A2A]/20',
  info: 'border-[#2A2A2A]/20',
};

const RESOURCES = [
  { title: 'Food Cost Formula Guide', desc: 'How to calculate and control food cost step by step.', url: 'https://pos.toasttab.com/blog/on-the-line/food-cost-percentage', source: 'Toast' },
  { title: 'Prime Cost Explained', desc: 'Why prime cost is the #1 number in your restaurant.', url: 'https://www.restaurantowner.com/public/Prime-Cost-The-Most-Important-Number.cfm', source: 'RestaurantOwner.com' },
  { title: 'Menu Engineering 101', desc: 'Stars, Plowhorses, Puzzles & Dogs.', url: 'https://pos.toasttab.com/blog/on-the-line/menu-engineering', source: 'Toast' },
  { title: 'Restaurant P&L Guide', desc: 'How to read and improve your profit & loss statement.', url: 'https://www.restaurant365.com/blog/average-restaurant-profit-margin/', source: 'Restaurant365' },
  { title: 'Break-Even Calculator Guide', desc: 'Understanding and using your break-even point.', url: 'https://www.sba.gov/business-guide/plan-your-business/calculate-your-startup-costs', source: 'SBA.gov' },
  { title: 'Portion Control Best Practices', desc: 'Consistency drives repeat business and controls cost.', url: 'https://www.webstaurantstore.com/article/portion-control-guide.html', source: 'WebstaurantStore' },
];

const FIXED_COST_FIELDS = [
  { key: 'rent', label: 'Monthly Rent', placeholder: 'e.g. 4500' },
  { key: 'utilities', label: 'Utilities (gas, electric, water)', placeholder: 'e.g. 800' },
  { key: 'insurance', label: 'Insurance', placeholder: 'e.g. 350' },
  { key: 'payroll', label: 'Fixed Payroll (salaried staff)', placeholder: 'e.g. 8500' },
  { key: 'managerSalary', label: 'Manager Salary', placeholder: 'e.g. 4200' },
  { key: 'loanPayments', label: 'Loan / Lease Payments', placeholder: 'e.g. 600' },
  { key: 'software', label: 'Software / POS / Subscriptions', placeholder: 'e.g. 250' },
  { key: 'otherFixed', label: 'Other Monthly Costs', placeholder: 'e.g. 300' },
];

export default function GuidedWizard() {
  const navigate = useNavigate();
  const {
    ingredients, addIngredient,
    recipes, addRecipe,
    laborSettings, setLaborSettings,
    breakEvenData, setBreakEvenData,
    pnlData, loadDemoData,
  } = useApp();

  const [step, setStep] = useState(0);
  const [headache, setHeadache] = useState('');
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactInfo, setContactInfo] = useState(null);

  const [ingForm, setIngForm] = useState({
    name: '', vendor: '', category: 'Other',
    purchaseCost: '', purchaseQuantity: '', purchaseUnit: 'lb', yieldPercent: 100, notes: '',
  });

  const [recipeForm, setRecipeForm] = useState({
    name: '', category: 'Pizza', menuPrice: '',
    packagingCost: '0.84', prepMinutes: '', cookMinutes: '',
    laborWage: '16', laborBurdenPercent: '25',
    recipeIngredients: [],
  });

  const nextStep = useCallback(() => setStep(s => Math.min(s + 1, STEPS.length - 1)), []);
  const prevStep = useCallback(() => setStep(s => Math.max(s - 1, 0)), []);

  const handleAddIngredient = useCallback(() => {
    if (!ingForm.name.trim()) return;
    addIngredient({
      ...ingForm,
      purchaseCost: Number(ingForm.purchaseCost) || 0,
      purchaseQuantity: Number(ingForm.purchaseQuantity) || 1,
      yieldPercent: Number(ingForm.yieldPercent) || 100,
    });
    setIngForm({ name: '', vendor: '', category: 'Other', purchaseCost: '', purchaseQuantity: '', purchaseUnit: 'lb', yieldPercent: 100, notes: '' });
  }, [ingForm, addIngredient]);

  const addRecIng = useCallback(() => {
    if (ingredients.length === 0) return;
    setRecipeForm(f => ({
      ...f,
      recipeIngredients: [...f.recipeIngredients, {
        id: Date.now().toString(36),
        ingredientId: ingredients[0].id, amount: '', unit: ingredients[0].purchaseUnit,
      }],
    }));
  }, [ingredients]);

  const updateRecIng = useCallback((i, field, val) => {
    setRecipeForm(f => {
      const updated = [...f.recipeIngredients];
      updated[i] = { ...updated[i], [field]: val };
      if (field === 'ingredientId') {
        const ing = ingredients.find(x => x.id === val);
        if (ing) updated[i].unit = ing.purchaseUnit;
      }
      return { ...f, recipeIngredients: updated };
    });
  }, [ingredients]);

  const removeRecIng = useCallback((i) => {
    setRecipeForm(f => ({ ...f, recipeIngredients: f.recipeIngredients.filter((_, idx) => idx !== i) }));
  }, []);

  const recipeCosts = useMemo(() => {
    let ingredientCost = 0;
    for (const ri of recipeForm.recipeIngredients) {
      const ing = ingredients.find(i => i.id === ri.ingredientId);
      if (!ing) continue;
      ingredientCost += convertUnit(Number(ri.amount) || 0, ri.unit, ing.purchaseUnit) * ing.costPerUsableUnit;
    }
    const packaging = Number(recipeForm.packagingCost) || 0;
    const totalMin = (Number(recipeForm.prepMinutes) || 0) + (Number(recipeForm.cookMinutes) || 0);
    const loaded = (Number(recipeForm.laborWage) || 0) * (1 + (Number(recipeForm.laborBurdenPercent) || 0) / 100);
    const labor = loaded * totalMin / 60;
    const total = ingredientCost + packaging + labor;
    const price = Number(recipeForm.menuPrice) || 0;
    const profit = price - total;
    const foodPct = price > 0 ? (ingredientCost / price) * 100 : 0;
    const primePct = price > 0 ? ((ingredientCost + labor) / price) * 100 : 0;
    const margin = price > 0 ? (profit / price) * 100 : 0;
    return { ingredientCost, packaging, labor, total, profit, foodPct, primePct, margin, price };
  }, [recipeForm, ingredients]);

  const handleSaveRecipe = useCallback(() => {
    if (!recipeForm.name.trim()) return;
    addRecipe({
      ...recipeForm,
      menuPrice: Number(recipeForm.menuPrice) || 0,
      packagingCost: Number(recipeForm.packagingCost) || 0,
      prepMinutes: Number(recipeForm.prepMinutes) || 0,
      cookMinutes: Number(recipeForm.cookMinutes) || 0,
      laborWage: Number(recipeForm.laborWage) || 0,
      laborBurdenPercent: Number(recipeForm.laborBurdenPercent) || 0,
      ingredientCost: recipeCosts.ingredientCost,
      totalCost: recipeCosts.total,
      grossProfit: recipeCosts.profit,
      grossMargin: recipeCosts.margin,
      foodCostPercent: recipeCosts.foodPct,
      primeCostPercent: recipeCosts.primePct,
      laborCost: recipeCosts.labor,
      targetFoodCostPercent: 30,
      targetPrimeCostPercent: 60,
    });
  }, [recipeForm, recipeCosts, addRecipe]);

  const handleSaveLabor = useCallback(() => {
    setLaborSettings({
      defaultWage: Number(recipeForm.laborWage) || 15,
      laborBurdenPercent: Number(recipeForm.laborBurdenPercent) || 25,
    });
  }, [recipeForm.laborWage, recipeForm.laborBurdenPercent, setLaborSettings]);

  const recommendations = useMemo(() => {
    return generateRecommendations({ recipes, ingredients, breakEvenData, pnlData });
  }, [recipes, ingredients, breakEvenData, pnlData]);

  const loadedRate = (Number(recipeForm.laborWage) || 0) * (1 + (Number(recipeForm.laborBurdenPercent) || 0) / 100);

  const avgFoodCost = recipes.length > 0
    ? (recipes.reduce((s, r) => s + (r.foodCostPercent || 0), 0) / recipes.length) : 0;

  const breakEvenOrders = useMemo(() => {
    const totalFixed = FIXED_COST_FIELDS.reduce((s, f) => s + (Number(breakEvenData[f.key]) || 0), 0);
    const margin = (Number(breakEvenData.avgContributionMargin) || 1) / 100;
    const avgOrd = Number(breakEvenData.avgOrderValue) || 1;
    const beDaily = totalFixed > 0 ? (totalFixed / margin) / 30 : 0;
    return Math.ceil(beDaily / avgOrd);
  }, [breakEvenData]);

  const progress = ((step + 1) / STEPS.length) * 100;
  const recipeSaved = recipes.some(r => r.name === recipeForm.name);

  return (
    <div className="page-enter p-4 md:p-8 max-w-3xl mx-auto">
      {/* Progress bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const active = i === step;
            const done = i < step;
            return (
              <button key={s.id} onClick={() => setStep(i)}
                className={`flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full transition-all ${active ? 'bg-[#E23D28] text-white' : done ? 'bg-[#2E7D32] text-white' : 'text-[#2A2A2A]/40'}`}
                data-testid={`wizard-step-${s.id}`}
              >
                <Icon className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">{s.label}</span>
              </button>
            );
          })}
        </div>
        <div className="h-2 bg-[#F0E6D2] rounded-full border border-[#2A2A2A]/10 overflow-hidden">
          <div className="h-full bg-[#E23D28] rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {/* STEP 0: Welcome */}
      {step === 0 && (
        <WizardWelcome headache={headache} setHeadache={setHeadache} onNext={nextStep}
          onLoadDemo={() => { loadDemoData(); setStep(5); }} />
      )}

      {/* STEP 1: Ingredients */}
      {step === 1 && (
        <WizardIngredients ingredients={ingredients} ingForm={ingForm} setIngForm={setIngForm}
          onAddIngredient={handleAddIngredient} onNext={nextStep} onBack={prevStep} />
      )}

      {/* STEP 2: Recipe */}
      {step === 2 && (
        <div data-testid="wizard-recipe">
          <div className="pizza-card p-6 mb-4">
            <h2 className="text-3xl text-[#2A2A2A] mb-2" style={{ fontFamily: "'Bebas Neue', cursive" }}>
              Build Your #1 Seller
            </h2>
            <p className="text-sm text-[#2A2A2A]/60 mb-4">
              Pick your most popular menu item — the one you sell the most of. We'll figure out exactly what it costs to make.
            </p>
            <WhyBox title="Why This Is The Most Important Step">
              <p>Most restaurant owners know what they <em>charge</em> for an item. Very few know what it actually <em>costs</em> to make — including ingredients, packaging, AND the labor to prep and cook it.</p>
              <p>We'll calculate your <strong>food cost %</strong>, <strong>labor cost per item</strong>, and <strong>suggested menu price</strong> in real-time as you add ingredients.</p>
            </WhyBox>

            <div className="space-y-4 mb-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#2A2A2A]/60 mb-0.5 block">What's it called?</label>
                  <input type="text" placeholder="e.g. Large Cheese Pizza" value={recipeForm.name}
                    onChange={e => setRecipeForm(f => ({ ...f, name: e.target.value }))}
                    className="pizza-input w-full text-sm" data-testid="wizard-recipe-name" />
                </div>
                <div>
                  <label className="text-xs text-[#2A2A2A]/60 mb-0.5 block">What do you charge? ($)</label>
                  <input type="number" step="0.01" min="0" placeholder="e.g. 16.99" value={recipeForm.menuPrice}
                    onChange={e => setRecipeForm(f => ({ ...f, menuPrice: e.target.value }))}
                    className="pizza-input w-full text-sm" data-testid="wizard-recipe-price" />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold text-[#2A2A2A]" style={{ fontFamily: "'Bebas Neue', cursive" }}>What goes in it?</label>
                  <button type="button" onClick={addRecIng} disabled={ingredients.length === 0}
                    className="pizza-btn-secondary text-xs px-3 py-1 flex items-center gap-1" data-testid="wizard-add-rec-ing">
                    <Plus className="w-3 h-3" /> Add
                  </button>
                </div>
                {recipeForm.recipeIngredients.map((ri, i) => (
                  <div key={ri.id || `ri-${i}`} className="flex items-end gap-2 mb-2 p-2 bg-[#FFF8E7] rounded-lg border border-[#2A2A2A]/10">
                    <div className="flex-1 min-w-0">
                      <Select value={ri.ingredientId} onValueChange={v => updateRecIng(i, 'ingredientId', v)}>
                        <SelectTrigger className="border-2 border-[#2A2A2A] rounded-lg text-sm h-9" data-testid={`wizard-rec-ing-${i}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ingredients.map(x => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-20">
                      <input type="number" step="0.01" min="0" placeholder="Amt" value={ri.amount}
                        onChange={e => updateRecIng(i, 'amount', e.target.value)}
                        className="pizza-input w-full text-sm h-9" data-testid={`wizard-rec-amt-${i}`} />
                    </div>
                    <div className="w-20">
                      <Select value={ri.unit} onValueChange={v => updateRecIng(i, 'unit', v)}>
                        <SelectTrigger className="border-2 border-[#2A2A2A] rounded-lg text-sm h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>{UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <button onClick={() => removeRecIng(i)} className="p-1 hover:bg-red-100 rounded"><X className="w-4 h-4 text-[#E23D28]" /></button>
                  </div>
                ))}
                {recipeForm.recipeIngredients.length === 0 && ingredients.length > 0 && (
                  <p className="text-xs text-[#2A2A2A]/40 italic">Tap "Add" above to pick ingredients for this recipe.</p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#2A2A2A]/60 mb-0.5 block">Prep time (minutes)</label>
                  <input type="number" min="0" placeholder="e.g. 5" value={recipeForm.prepMinutes}
                    onChange={e => setRecipeForm(f => ({ ...f, prepMinutes: e.target.value }))}
                    className="pizza-input w-full text-sm" data-testid="wizard-recipe-prep" />
                </div>
                <div>
                  <label className="text-xs text-[#2A2A2A]/60 mb-0.5 block">Cook time (minutes)</label>
                  <input type="number" min="0" placeholder="e.g. 12" value={recipeForm.cookMinutes}
                    onChange={e => setRecipeForm(f => ({ ...f, cookMinutes: e.target.value }))}
                    className="pizza-input w-full text-sm" data-testid="wizard-recipe-cook" />
                </div>
              </div>
            </div>

            {recipeForm.recipeIngredients.length > 0 && (
              <div className="bg-[#2A2A2A] rounded-xl p-4 text-white mb-4" data-testid="wizard-recipe-costs">
                <p className="text-xs text-white/50 mb-2 uppercase" style={{ fontFamily: "'Bebas Neue', cursive" }}>Live Cost Preview</p>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div>
                    <p className="text-xs text-white/50">Ingredient Cost</p>
                    <p className="text-lg font-bold text-[#FFC107]" style={{ fontFamily: "'Bebas Neue', cursive" }}>{formatCurrency(recipeCosts.ingredientCost)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-white/50">Total Cost</p>
                    <p className="text-lg font-bold text-[#E23D28]" style={{ fontFamily: "'Bebas Neue', cursive" }}>{formatCurrency(recipeCosts.total)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-white/50">Your Profit</p>
                    <p className={`text-lg font-bold ${recipeCosts.profit >= 0 ? 'text-[#4CAF50]' : 'text-[#E23D28]'}`} style={{ fontFamily: "'Bebas Neue', cursive" }}>{formatCurrency(recipeCosts.profit)}</p>
                  </div>
                </div>
                {recipeCosts.foodPct > 0 && (
                  <p className="text-xs text-white/60 text-center mt-2">
                    Food Cost: <strong className={recipeCosts.foodPct > 35 ? 'text-[#E23D28]' : 'text-[#4CAF50]'}>{recipeCosts.foodPct.toFixed(1)}%</strong>
                    {recipeCosts.foodPct > 35 && " — that's high. Target: under 30%."}
                  </p>
                )}
              </div>
            )}

            {recipeForm.name.trim() && recipeForm.recipeIngredients.length > 0 && !recipeSaved && (
              <button onClick={handleSaveRecipe} className="pizza-btn text-xs w-full mb-3" data-testid="wizard-save-recipe-btn">
                <Check className="w-3.5 h-3.5 inline mr-1" /> Save This Recipe
              </button>
            )}
            {recipeSaved && (
              <p className="text-xs text-[#2E7D32] flex items-center gap-1 mb-3">
                <CheckCircle className="w-4 h-4" /> Recipe saved! You can add more from the Recipe Builder later.
              </p>
            )}

            <div className="border-t border-[#F0E6D2] pt-3">
              <button onClick={downloadRecipeTemplate}
                className="pizza-btn-secondary text-xs px-4 py-1.5 flex items-center gap-1" data-testid="wizard-download-recipe-template">
                <Download className="w-3.5 h-3.5" /> Download Recipe Template
              </button>
            </div>
          </div>
          <div className="flex justify-between">
            <button onClick={prevStep} className="pizza-btn-secondary text-sm flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Back</button>
            <button onClick={() => { handleSaveLabor(); nextStep(); }} className="pizza-btn text-sm flex items-center gap-1">Next: Labor Costs <ArrowRight className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      {/* STEP 3: Labor */}
      {step === 3 && (
        <div data-testid="wizard-labor">
          <div className="pizza-card p-6 mb-4">
            <h2 className="text-3xl text-[#2A2A2A] mb-2" style={{ fontFamily: "'Bebas Neue', cursive" }}>Every Pizza Needs a Person</h2>
            <p className="text-sm text-[#2A2A2A]/60 mb-4">Labor is the other half of your "prime cost." Let's make sure we're counting it right.</p>
            <WhyBox title="What Is 'Loaded' Labor Cost?">
              <p>When you pay someone $16/hour, that's not what they actually cost you. On top of wages, you pay <strong>FICA taxes (7.65%)</strong>, <strong>workers' comp</strong>, <strong>unemployment tax</strong>, and sometimes <strong>benefits</strong>. That's the <strong>labor burden</strong>.</p>
              <p>A typical burden is 20-30%. So a $16/hour employee really costs $19.20-$20.80/hour.</p>
              <p><strong>Prime cost</strong> = Food + Labor. The NRA says keep this under 60%.</p>
              <p className="text-xs text-[#2A2A2A]/50 italic">Source: National Restaurant Association — prime cost target: 55-60%</p>
            </WhyBox>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-[#2A2A2A]/60 mb-0.5 block">What do you pay your line cooks per hour?</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#2A2A2A]/40">$</span>
                  <input type="number" step="0.01" min="0" value={recipeForm.laborWage}
                    onChange={e => setRecipeForm(f => ({ ...f, laborWage: e.target.value }))}
                    className="pizza-input w-full pl-7" data-testid="wizard-labor-wage" />
                </div>
              </div>
              <div>
                <label className="text-xs text-[#2A2A2A]/60 mb-0.5 block">Labor burden % (taxes, insurance, workers comp)</label>
                <input type="number" step="1" min="0" max="50" value={recipeForm.laborBurdenPercent}
                  onChange={e => setRecipeForm(f => ({ ...f, laborBurdenPercent: e.target.value }))}
                  className="pizza-input w-full" data-testid="wizard-labor-burden" />
                <p className="text-xs text-[#2A2A2A]/40 mt-1">Typical: 20-30%. Covers FICA, workers comp, and benefits.</p>
              </div>
              <div className="bg-[#2A2A2A] rounded-xl p-4 text-white">
                <p className="text-xs text-white/50 mb-1 uppercase" style={{ fontFamily: "'Bebas Neue', cursive" }}>Your Loaded Rate</p>
                <p className="text-3xl font-bold text-[#FFC107]" style={{ fontFamily: "'Bebas Neue', cursive" }}>${loadedRate.toFixed(2)}/hr</p>
                <p className="text-xs text-white/50 mt-1">That's what each labor hour really costs you.</p>
              </div>
            </div>
          </div>
          <div className="flex justify-between">
            <button onClick={prevStep} className="pizza-btn-secondary text-sm flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Back</button>
            <button onClick={() => { handleSaveLabor(); nextStep(); }} className="pizza-btn text-sm flex items-center gap-1">Next: Fixed Costs <ArrowRight className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      {/* STEP 4: Fixed Costs */}
      {step === 4 && (
        <div data-testid="wizard-costs">
          <div className="pizza-card p-6 mb-4">
            <h2 className="text-3xl text-[#2A2A2A] mb-2" style={{ fontFamily: "'Bebas Neue', cursive" }}>The Bills That Never Stop</h2>
            <p className="text-sm text-[#2A2A2A]/60 mb-4">These hit every month no matter what — whether you sell 10 pizzas or 1,000.</p>
            <WhyBox title="What Is Break-Even & Why It Changes Everything">
              <p><strong>Break-even</strong> is the exact dollar amount you need to sell every month just to cover all your costs. Below that, you lose money. Above it, you profit.</p>
              <p>Knowing this tells you exactly how many orders you need per day. It turns "am I doing okay?" into a real, countable target.</p>
              <p className="text-xs text-[#2A2A2A]/50 italic">Source: SBA Small Business Financial Planning Guide</p>
            </WhyBox>
            <div className="space-y-3">
              {FIXED_COST_FIELDS.map(({ key, label, placeholder }) => (
                <div key={key} className="flex items-center gap-3">
                  <label className="w-56 text-sm text-[#2A2A2A]/70 flex-shrink-0">{label}</label>
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#2A2A2A]/40 text-sm">$</span>
                    <input type="number" step="0.01" min="0" placeholder={placeholder}
                      value={breakEvenData[key]}
                      onChange={e => setBreakEvenData(prev => ({ ...prev, [key]: e.target.value }))}
                      className="pizza-input w-full pl-7 text-sm" data-testid={`wizard-cost-${key}`} />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 pt-4 border-t border-[#F0E6D2]">
              <p className="text-sm font-semibold text-[#2A2A2A] mb-3" style={{ fontFamily: "'Bebas Neue', cursive" }}>Two more numbers to find your break-even:</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#2A2A2A]/60 mb-0.5 block">Average Order Value ($)</label>
                  <input type="number" step="0.01" min="0.01" placeholder="e.g. 28" value={breakEvenData.avgOrderValue}
                    onChange={e => setBreakEvenData(prev => ({ ...prev, avgOrderValue: e.target.value }))}
                    className="pizza-input w-full text-sm" data-testid="wizard-cost-aov" />
                </div>
                <div>
                  <label className="text-xs text-[#2A2A2A]/60 mb-0.5 block">Avg Contribution Margin %</label>
                  <input type="number" step="1" min="1" max="100" placeholder="e.g. 62" value={breakEvenData.avgContributionMargin}
                    onChange={e => setBreakEvenData(prev => ({ ...prev, avgContributionMargin: e.target.value }))}
                    className="pizza-input w-full text-sm" data-testid="wizard-cost-margin" />
                  <p className="text-xs text-[#2A2A2A]/40 mt-0.5">% of each sale left after variable costs. Typical pizza shop: 55-70%. Not sure? Use 62%.</p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-between">
            <button onClick={prevStep} className="pizza-btn-secondary text-sm flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Back</button>
            <button onClick={nextStep} className="pizza-btn text-sm flex items-center gap-1" data-testid="wizard-see-results-btn">
              <Sparkles className="w-4 h-4" /> See My Report Card
            </button>
          </div>
        </div>
      )}

      {/* STEP 5: Results */}
      {step === 5 && (
        <div data-testid="wizard-results">
          <div className="text-center mb-6">
            <h2 className="text-4xl text-[#2A2A2A] mb-2" style={{ fontFamily: "'Bebas Neue', cursive" }}>Your Pizza Report Card</h2>
            <p className="text-sm text-[#2A2A2A]/60">Here's the truth about your numbers. No sugarcoating.</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <div className="stat-card text-center"><p className="text-xs text-[#2A2A2A]/50 uppercase" style={{ fontFamily: "'Bebas Neue', cursive" }}>Ingredients</p><p className="text-2xl font-bold text-[#2A2A2A]" style={{ fontFamily: "'Bebas Neue', cursive" }}>{ingredients.length}</p></div>
            <div className="stat-card text-center"><p className="text-xs text-[#2A2A2A]/50 uppercase" style={{ fontFamily: "'Bebas Neue', cursive" }}>Recipes</p><p className="text-2xl font-bold text-[#2A2A2A]" style={{ fontFamily: "'Bebas Neue', cursive" }}>{recipes.length}</p></div>
            <div className="stat-card text-center"><p className="text-xs text-[#2A2A2A]/50 uppercase" style={{ fontFamily: "'Bebas Neue', cursive" }}>Avg Food Cost</p><p className={`text-2xl font-bold ${avgFoodCost > 30 ? 'text-[#E23D28]' : 'text-[#2E7D32]'}`} style={{ fontFamily: "'Bebas Neue', cursive" }}>{avgFoodCost.toFixed(1)}%</p></div>
            <div className="stat-card text-center"><p className="text-xs text-[#2A2A2A]/50 uppercase" style={{ fontFamily: "'Bebas Neue', cursive" }}>Break-Even</p><p className="text-2xl font-bold text-[#E23D28]" style={{ fontFamily: "'Bebas Neue', cursive" }}>{breakEvenOrders}/day</p></div>
          </div>

          {/* Recommendations */}
          <div className="space-y-4 mb-6" data-testid="wizard-recommendations">
            {recommendations.map((tip, i) => {
              const TipIcon = TIP_ICONS[tip.type] || Target;
              return (
                <div key={`tip-${tip.type}-${i}`} className={`pizza-card p-5 border-l-4 ${TIP_BORDERS[tip.type] || 'border-[#2A2A2A]/20'}`} data-testid={`wizard-tip-${i}`}>
                  <div className="flex items-start gap-3">
                    <TipIcon className={`w-5 h-5 ${TIP_COLORS[tip.type] || 'text-[#2A2A2A]'}`} />
                    <div className="flex-1">
                      <h3 className="text-lg text-[#2A2A2A] mb-1" style={{ fontFamily: "'Bebas Neue', cursive" }}>{tip.title}</h3>
                      {tip.why && (
                        <div className="bg-[#FFF8E7] rounded-lg p-3 mb-3 border border-[#2A2A2A]/5">
                          <p className="text-xs font-bold text-[#2A2A2A]/60 mb-1 flex items-center gap-1" style={{ fontFamily: "'Bebas Neue', cursive" }}>
                            <Info className="w-3 h-3" /> Why This Matters
                          </p>
                          <p className="text-sm text-[#2A2A2A]/70">{tip.why}</p>
                        </div>
                      )}
                      <p className="text-sm text-[#2A2A2A]/70 mb-3">{tip.detail}</p>
                      <ul className="space-y-1.5 mb-3">
                        {tip.actions.map((action, j) => (
                          <li key={`action-${i}-${j}`} className="flex items-start gap-2 text-sm text-[#2A2A2A]/80">
                            <ArrowRight className="w-3.5 h-3.5 text-[#E23D28] flex-shrink-0 mt-0.5" /><span>{action}</span>
                          </li>
                        ))}
                      </ul>
                      {(tip.source || tip.learnMore) && (
                        <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-[#F0E6D2]">
                          {tip.source && <span className="text-xs text-[#2A2A2A]/40 flex items-center gap-1"><BookOpen className="w-3 h-3" /> {tip.source}</span>}
                          {tip.learnMore && <a href={tip.learnMore} target="_blank" rel="noopener noreferrer" className="text-xs text-[#E23D28] hover:underline flex items-center gap-1 font-medium" data-testid={`wizard-tip-link-${i}`}><ExternalLink className="w-3 h-3" /> Learn More</a>}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Resources */}
          <div className="pizza-card p-6 mb-6" data-testid="wizard-resources">
            <div className="flex items-center gap-2 mb-4"><BookOpen className="w-5 h-5 text-[#E23D28]" /><h3 className="text-2xl text-[#2A2A2A]" style={{ fontFamily: "'Bebas Neue', cursive" }}>Keep Learning</h3></div>
            <p className="text-sm text-[#2A2A2A]/60 mb-4">The best operators never stop learning. Hand-picked resources:</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {RESOURCES.map((res) => (
                <a key={res.url} href={res.url} target="_blank" rel="noopener noreferrer"
                  className="flex items-start gap-3 p-3 rounded-lg border-2 border-[#2A2A2A]/10 hover:border-[#E23D28] hover:bg-[#E23D28]/5 transition-all group">
                  <ExternalLink className="w-4 h-4 text-[#2A2A2A]/30 group-hover:text-[#E23D28] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-[#2A2A2A] group-hover:text-[#E23D28]">{res.title}</p>
                    <p className="text-xs text-[#2A2A2A]/50">{res.desc}</p>
                    <p className="text-xs text-[#2A2A2A]/50 mt-0.5">{res.source}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* Share/Download */}
          <div className="pizza-card p-6 text-center">
            <h3 className="text-2xl text-[#2A2A2A] mb-2" style={{ fontFamily: "'Bebas Neue', cursive" }}>Share This Report</h3>
            <p className="text-sm text-[#2A2A2A]/60 mb-4">Download a branded PDF to share with partners, investors, or your management team.</p>
            <div className="flex flex-wrap gap-3 justify-center mb-6">
              <button
                onClick={() => {
                  if (contactInfo) {
                    generateReport({ contact: contactInfo, recipes, ingredients, breakEvenData, pnlData, recommendations });
                  } else {
                    setShowContactForm(true);
                  }
                }}
                className="pizza-btn text-sm flex items-center gap-2" data-testid="wizard-download-report-btn"
              >
                <Download className="w-4 h-4" /> Download Report PDF
              </button>
            </div>
            {contactInfo && <p className="text-xs text-[#2E7D32] flex items-center justify-center gap-1 mb-4"><CheckCircle className="w-3.5 h-3.5" /> Saved! Report will include your contact info.</p>}
            <div className="border-t border-[#F0E6D2] pt-5">
              <h3 className="text-2xl text-[#2A2A2A] mb-2" style={{ fontFamily: "'Bebas Neue', cursive" }}>Ready to Dig Deeper?</h3>
              <p className="text-sm text-[#2A2A2A]/60 mb-4">Jump into the full dashboard for detailed analysis.</p>
              <div className="flex flex-wrap gap-3 justify-center">
                <button onClick={() => navigate('/dashboard')} className="pizza-btn-secondary text-sm" data-testid="wizard-go-dashboard-btn">Go to Dashboard</button>
                <button onClick={() => navigate('/recipes')} className="pizza-btn-secondary text-sm" data-testid="wizard-go-recipes-btn">Recipe Builder</button>
                <button onClick={() => navigate('/reports')} className="pizza-btn-secondary text-sm">P&L Reports</button>
              </div>
            </div>
          </div>

          {showContactForm && (
            <ContactFormModal
              onClose={() => setShowContactForm(false)}
              onSuccess={(info) => {
                setContactInfo(info);
                setShowContactForm(false);
                setTimeout(() => {
                  generateReport({ contact: info, recipes, ingredients, breakEvenData, pnlData, recommendations });
                }, 300);
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}
